import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Cpu,
  Cloud,
  BarChart3,
  Shield,
  Zap,
  Brain,
  ChevronRight,
  Sparkles,
  ArrowRight,
  Smartphone,
  Code2,
  Users,
} from "lucide-react";

import PageHero from "@/components/shared/PageHero";
import Section from "@/components/shared/Section";
import SectionHeader from "@/components/shared/SectionHeader";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import Eyebrow from "@/components/shared/Eyebrow";

const SOLUTIONS = [
  { icon: Cpu,       title: "Custom AI Development",   desc: "Tailored AI models for your business" },
  { icon: Cloud,     title: "AI Cloud Transformation", desc: "Scalable AI systems"                 },
  { icon: BarChart3, title: "Data Intelligence",       desc: "Insights from data"                  },
  { icon: Shield,    title: "Risk & Compliance AI",    desc: "Predict risks early"                 },
  { icon: Zap,       title: "Process Automation",      desc: "Automate workflows"                  },
  { icon: Brain,     title: "AI Strategy Consulting",  desc: "Roadmaps to adopt AI effectively"    },
];

const TEAM = [
  { icon: Smartphone, title: "iOS Developers",         desc: "Swift and SwiftUI specialists focused on secure, polished mobile experiences." },
  { icon: Code2,      title: "Android Developers",     desc: "Kotlin-first engineering for scalable Android apps with fast iteration cycles." },
  { icon: Brain,      title: "AI-Integrated Developers", desc: "AI features embedded into products with reliable model integration and monitoring." },
];

const WHY = [
  { title: "Faster Decisions",      desc: "Automate insights to accelerate decision cycles across teams." },
  { title: "Lower Risk",            desc: "Proactive risk detection to reduce losses and ensure compliance." },
  { title: "Scale with Confidence", desc: "Cloud-native architectures for secure, scalable deployments." },
];

const OUTCOMES = [
  {
    title: "Fraud Detection",
    desc: "Delivered a machine learning model that reduced fraud by 80% and improved detection latency from hours to minutes.",
    metric: "80% reduction",
  },
  {
    title: "Audit Automation",
    desc: "Automated audit workflows that saved 2000+ analyst hours annually while improving coverage.",
    metric: "2,000+ hours saved",
  },
];

const Solutions = () => {
  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      <PageHero
        eyebrow="Solutions"
        eyebrowIcon={Sparkles}
        title={
          <>
            Solutions for{" "}
            <span className="gradient-text">Enterprise Growth</span>
          </>
        }
        description="Tailored AI products and platforms to automate processes, reduce risk, and enable data-driven decisions across your organization."
        actions={
          <>
            <Button asChild className="btn-primary rounded-full px-6 h-11">
              <Link to="/explore-solutions" className="inline-flex items-center gap-2">
                Explore solutions <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-outline rounded-full px-6 h-11">
              <Link to="/contact">Contact us</Link>
            </Button>
          </>
        }
      />

      {/* Solutions grid */}
      <Section>
        <SectionHeader
          eyebrow="Capabilities"
          eyebrowIcon={Brain}
          title={<>Built for <span className="gradient-text">scale and speed</span></>}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={i} variant="up" delay={i * 0.06}>
              <GlowCard className="p-7 h-full group flex flex-col">
                <div className="feature-icon feature-icon-lg mb-5 group-hover:scale-110 transition-transform">
                  <s.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed flex-1">{s.desc}</p>
                <div className="mt-5">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--brand-primary-light)] hover:gap-2.5 transition-all"
                  >
                    Request demo <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Expert team */}
      <Section>
        <Reveal variant="up">
          <GlowCard variant="liquid" className="aurora p-8 sm:p-12 overflow-hidden">
            <div className="relative z-10">
              <Eyebrow icon={Users} className="mb-4">Expert team</Eyebrow>
              <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3">
                A 50+ member <span className="gradient-text">delivery team</span>
              </h2>
              <p className="text-[var(--text-secondary)] mb-8 max-w-2xl">
                Expert iOS developers, Android developers, and AI-integrated engineers who build production-ready
                mobile apps and intelligent systems.
              </p>
              <div className="grid sm:grid-cols-3 gap-5">
                {TEAM.map((t, i) => (
                  <Reveal key={i} variant="up" delay={i * 0.08}>
                    <GlowCard className="p-6 h-full">
                      <div className="feature-icon mb-4">
                        <t.icon className="w-5 h-5" />
                      </div>
                      <h3 className="font-semibold mb-2">{t.title}</h3>
                      <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{t.desc}</p>
                    </GlowCard>
                  </Reveal>
                ))}
              </div>
            </div>
          </GlowCard>
        </Reveal>
      </Section>

      {/* Why it matters */}
      <Section>
        <SectionHeader
          eyebrow="Why it matters"
          eyebrowIcon={Sparkles}
          title={<>Outcomes that <span className="gradient-text">compound</span></>}
          description="We deliver measurable outcomes by combining domain expertise with scalable AI platforms — enabling faster decision-making, reduced operational costs and improved compliance."
        />
        <div className="grid sm:grid-cols-3 gap-5">
          {WHY.map((w, i) => (
            <Reveal key={i} variant="up" delay={i * 0.08}>
              <GlowCard className="p-7 h-full">
                <div className="text-2xl font-display font-bold mb-3 stat-number">0{i + 1}</div>
                <h3 className="font-semibold text-lg mb-2">{w.title}</h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{w.desc}</p>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Featured outcomes */}
      <Section>
        <SectionHeader
          eyebrow="Featured outcomes"
          eyebrowIcon={BarChart3}
          title={<>Real numbers, <span className="gradient-text">real impact</span></>}
        />
        <div className="grid md:grid-cols-2 gap-5">
          {OUTCOMES.map((o, i) => (
            <Reveal key={i} variant="up" delay={i * 0.08}>
              <GlowCard className="p-7 h-full">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h4 className="font-semibold text-xl">{o.title}</h4>
                  <span className="text-xs font-mono px-3 py-1 rounded-full border border-[color:var(--border-brand)] bg-[color:var(--brand-primary)]/10 text-[var(--brand-primary-light)]">
                    {o.metric}
                  </span>
                </div>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-5">{o.desc}</p>
                <Button asChild size="sm" variant="outline" className="btn-outline rounded-full px-4">
                  <Link to="/case-studies" className="inline-flex items-center gap-1.5">
                    Read case study <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </Button>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default Solutions;
